import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Button,
  Loader,
  Message,
  Segment,
  Table,
  Checkbox,
  Icon,
  Label,
  Header,
  Confirm,
} from 'semantic-ui-react';
import {
  contractService,
  EContractStatus,
  type ContractLineDetailsModalDto,
  type ContractLineDto,
  type ContractResponseDto,
} from '../../services/contracts/ContractService';
import { FastchipSwapModal } from './FastchipSwapModal';
import { FastChipMovementHistoryModal } from '../icCids/FastChipMovementHistoryModal';
import type { ICcIdResponse, FastchipIccidBatchMovementSourceForBypass } from '../../types/dtos';
import { icCidService } from '../../services/icCids/ICcIdService';
import { useToast } from '../../hooks/useToast';
import { formatCnpj } from '../../services/utils/stringUtils';
import {
  getContractStatusSemanticColor,
  getContractStatusText,
  isContractStatusAdministrativeFastchipSwapBlocked,
  normalizeContractStatus,
} from '../../utils/contractStatusUtils';

function lineIsAditivo(line: ContractLineDto): boolean {
  const ct = (line as ContractLineDto & { contractType?: string }).contractType;
  return line.negociacao === 'Aditivo' || ct === 'Aditivo';
}

function contractHeaderLooksLikeRenegotiation(dto: ContractLineDetailsModalDto): boolean {
  const h = `${dto.contractType ?? ''} ${dto.tipoContratacao ?? ''}`.toLowerCase();
  return h.includes('reneg');
}

function normalizeCcIdDigits(cc: string): string {
  return cc.replace(/\D/g, '');
}

export interface FastLinkedContractModalProps {
  open: boolean;
  onClose: () => void;
  focusContractId: string | null;
  isAdmin: boolean;
  /**
   * Origem RN02 quando admin usa bypass de estado do contrato na troca FAST em lote.
   * Deve refletir a página de abertura (ICcIdsModal / SuspensionsModal na API).
   */
  fastchipBatchBypassMovementSource: FastchipIccidBatchMovementSourceForBypass;
  /** Controlo ICCID quando aberto a partir da página ICCIDs (histórico directo). */
  historyIcCidControl?: ICcIdResponse | null;
  /** CCID da linha (Suspensões) para resolver o controlo ICCID antes de abrir o histórico. */
  fallbackHistoryChipCcId?: string | null;
  /** Incrementado pelo pai após troca bem-sucedida para recarregar linhas/modal. */
  reloadNonce?: number;
  onAfterSwapSuccess?: () => void;
  /** Página ICCIDs: cancelar contrato e libertar FAST (admin). */
  enableAdminContractCancel?: boolean;
  onAfterCancelContract?: () => void;
}

export const FastLinkedContractModal: React.FC<FastLinkedContractModalProps> = ({
  open,
  onClose,
  focusContractId,
  isAdmin,
  fastchipBatchBypassMovementSource,
  historyIcCidControl = null,
  fallbackHistoryChipCcId = null,
  reloadNonce = 0,
  onAfterSwapSuccess,
  enableAdminContractCancel = false,
  onAfterCancelContract,
}) => {
  const { showError, showSuccess } = useToast();
  const [loading, setLoading] = useState(false);
  const [groupLoadError, setGroupLoadError] = useState<string | null>(null);
  const [contractBase, setContractBase] = useState<ContractResponseDto | null>(null);
  const [focusDto, setFocusDto] = useState<ContractLineDetailsModalDto | null>(null);
  const [otherGroupDtos, setOtherGroupDtos] = useState<ContractLineDetailsModalDto[]>([]);
  const [selectedLineIds, setSelectedLineIds] = useState<Set<string>>(new Set());
  const [swapOpen, setSwapOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyIcCid, setHistoryIcCid] = useState<ICcIdResponse | null>(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const clientEstado = contractBase?.client?.estado ?? 'SP';

  const rn3Blocked = useMemo(
    () => isContractStatusAdministrativeFastchipSwapBlocked(contractBase?.status),
    [contractBase?.status]
  );

  const loadData = useCallback(async () => {
    if (!open || !focusContractId) return;
    setLoading(true);
    setGroupLoadError(null);
    setFocusDto(null);
    setContractBase(null);
    setOtherGroupDtos([]);
    setSelectedLineIds(new Set());
    try {
      const base = await contractService.getContractById(focusContractId);
      setContractBase(base);
      const primary = await contractService.getContractForLineDetailsModal(focusContractId);
      setFocusDto(primary);
      if (base.groupId) {
        try {
          const grp = await contractService.getGroupLineDetailsModal(base.groupId);
          setOtherGroupDtos(grp.filter((d) => d.id !== focusContractId));
        } catch {
          setOtherGroupDtos([]);
          setGroupLoadError(
            'Não foi possível carregar o resumo completo do grupo (GET /contracts/group/{groupId}/line-details-modal). A vista do contrato em foco mantém-se disponível.'
          );
        }
      } else {
        setOtherGroupDtos([]);
      }
    } catch {
      showError('Não foi possível carregar os dados do contrato.');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [open, focusContractId, onClose, showError]);

  useEffect(() => {
    void loadData();
  }, [loadData, reloadNonce]);

  const openHistory = useCallback(async () => {
    if (historyIcCidControl) {
      setHistoryIcCid(historyIcCidControl);
      setHistoryOpen(true);
      return;
    }
    const raw = fallbackHistoryChipCcId?.trim();
    if (!raw) {
      showError('Não há CCID associado para abrir o histórico FAST desta vista.');
      return;
    }
    const digits = normalizeCcIdDigits(raw);
    try {
      const res = await icCidService.searchICcIds({ ccId: digits || raw }, 1, 20, 'createdAt', true);
      const exact =
        res.items.find((x) => normalizeCcIdDigits(x.ccId) === digits) ??
        res.items.find((x) => x.ccId === raw);
      if (!exact) {
        showError('Não foi encontrado um controlo ICCID correspondente a este CCID.');
        return;
      }
      setHistoryIcCid(exact);
      setHistoryOpen(true);
    } catch {
      showError('Erro ao resolver o ICCID para histórico.');
    }
  }, [historyIcCidControl, fallbackHistoryChipCcId, showError]);

  const handleSwapConfirm = async (
    swaps: Array<{ lineId: string; contractId?: string; newIccid: string | null; motivoTroca: string; fastUsado: boolean }>
  ) => {
    if (!focusContractId) return;
    const items = swaps.map((s) => ({
      lineId: s.lineId,
      newIccid: s.newIccid,
      motivoTroca: s.motivoTroca,
      fastUsado: s.fastUsado,
    }));
    const bypass = isAdmin && !rn3Blocked;
    const n = swaps.length;
    try {
      await contractService.batchUpdateLineIccid(focusContractId, items, {
        allowAdministrativeContractStatusBypass: bypass,
        ...(bypass ? { movementSource: fastchipBatchBypassMovementSource } : {}),
      });
      showSuccess(
        `${n} FastChip${n > 1 ? 's' : ''} troca${n > 1 ? 'dos' : 'do'} com sucesso!`
      );
      setSelectedLineIds(new Set());
      setSwapOpen(false);
      onAfterSwapSuccess?.();
      await loadData();
    } catch (e: unknown) {
      const msg =
        e instanceof Error
          ? e.message
          : 'Erro ao processar a troca de FastChip(s).';
      showError(msg);
      throw e instanceof Error ? e : new Error(msg);
    }
  };

  const executeCancelContract = async () => {
    if (!focusContractId) return;
    setCancelLoading(true);
    try {
      await contractService.updateContractStatus(focusContractId, EContractStatus.Cancelado);
      showSuccess('Contrato cancelado. O FAST será libertado conforme a regra do sistema.');
      setCancelConfirmOpen(false);
      onAfterCancelContract?.();
      onClose();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Erro ao cancelar contrato.';
      showError(msg);
    } finally {
      setCancelLoading(false);
    }
  };

  const renderLineRow = (
    line: ContractLineDto,
    rowKey: string,
    opts: {
      showCheckboxColumn: boolean;
      allowRowCheckbox: boolean;
    }
  ) => {
    const { showCheckboxColumn, allowRowCheckbox } = opts;
    const isMarkedForSuspension = !!(line.excluirDoContrato || line.requerSuspensao);
    const isAditivo = lineIsAditivo(line);
    const ext = line as ContractLineDto & { isEsim?: boolean };
    return (
      <Table.Row key={rowKey} warning={isMarkedForSuspension}>
        {showCheckboxColumn && (
          <Table.Cell textAlign="center" collapsing>
            {allowRowCheckbox && isAditivo && !isMarkedForSuspension ? (
              <Checkbox
                checked={selectedLineIds.has(line.id)}
                onChange={(_, d) => {
                  setSelectedLineIds((prev) => {
                    const n = new Set(prev);
                    if (d.checked) n.add(line.id);
                    else n.delete(line.id);
                    return n;
                  });
                }}
              />
            ) : null}
          </Table.Cell>
        )}
        <Table.Cell textAlign="center">
          {isMarkedForSuspension ? (
            <Label color="red" size="small">
              A cancelar
            </Label>
          ) : (
            <Label color="green" size="small">
              Ativa
            </Label>
          )}
        </Table.Cell>
        <Table.Cell>
          <Label color={isAditivo ? 'green' : 'blue'} size="small">
            {line.negociacao ?? '—'}
          </Label>
        </Table.Cell>
        <Table.Cell>{line.ddd ?? '—'}</Table.Cell>
        <Table.Cell>
          <strong>{line.numero ?? '—'}</strong>
        </Table.Cell>
        <Table.Cell>{line.planName || line.plano || 'N/A'}</Table.Cell>
        <Table.Cell>{line.quantidade ?? 1}</Table.Cell>
        <Table.Cell>
          R${' '}
          {(line.valorTotalMensal ?? line.valorMensal ?? 0).toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Table.Cell>
        <Table.Cell>{line.tipoVenda ?? '—'}</Table.Cell>
        <Table.Cell>
          <code>{line.chipCcid ?? '—'}</code>
          {ext.isEsim ? (
            <Label size="mini" style={{ marginLeft: 6 }}>
              e-SIM
            </Label>
          ) : null}
        </Table.Cell>
        <Table.Cell>
          {line.ehPortabilidade ? (
            <span style={{ fontSize: '0.85em' }}>{line.portabilidadeOperadora ?? 'Sim'}</span>
          ) : (
            '—'
          )}
        </Table.Cell>
      </Table.Row>
    );
  };

  const renderContractBlock = (dto: ContractLineDetailsModalDto, mode: 'focus' | 'groupOther') => {
    const lines = dto.linhas ?? [];
    const isFocus = mode === 'focus';
    const renegHeader = contractHeaderLooksLikeRenegotiation(dto);
    const showSwapCol =
      isFocus && dto.fastchip && isAdmin && !rn3Blocked && dto.id === focusContractId;
    const groupReadOnlyMsg =
      mode === 'groupOther'
        ? 'Só leitura — troca de Fastchip apenas no contrato FAST em foco, linhas Aditivo.'
        : null;

    return (
      <Segment key={dto.id} style={{ marginBottom: 16 }}>
        <Header as="h4">
          {dto.clientRazaoSocial ?? 'Cliente'}
          <Header.Subheader>
            CNPJ: {formatCnpj(dto.clientCnpj ?? '')}
            {dto.id ? (
              <span style={{ marginLeft: 8 }}>
                · Contrato <code>{dto.id.slice(0, 8)}</code>
              </span>
            ) : null}
            {dto.status !== undefined && dto.status !== null ? (
              <Label
                color={getContractStatusSemanticColor(dto.status)}
                style={{ marginLeft: 8 }}
                size="small"
              >
                {getContractStatusText(dto.status)}
              </Label>
            ) : null}
            {dto.fastchip ? (
              <Label color="blue" size="small" style={{ marginLeft: 8 }}>
                Fastchip
              </Label>
            ) : null}
            {renegHeader ? (
              <Label color="orange" size="small" style={{ marginLeft: 8 }}>
                Renegociação — sem troca
              </Label>
            ) : null}
          </Header.Subheader>
        </Header>
        {groupReadOnlyMsg ? (
          <Message info size="small">
            {groupReadOnlyMsg}
          </Message>
        ) : null}
        {isFocus && rn3Blocked && (
          <Message warning size="small">
            Contrato cancelado ou finalizado — troca administrativa FAST não disponível (RN03).
          </Message>
        )}
        <div style={{ overflowX: 'auto', maxHeight: '48vh' }}>
          <Table celled striped compact size="small">
            <Table.Header>
              <Table.Row>
                {showSwapCol ? <Table.HeaderCell collapsing /> : null}
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Negociação</Table.HeaderCell>
                <Table.HeaderCell>DDD</Table.HeaderCell>
                <Table.HeaderCell>Número</Table.HeaderCell>
                <Table.HeaderCell>Plano</Table.HeaderCell>
                <Table.HeaderCell>Qtd</Table.HeaderCell>
                <Table.HeaderCell>Valor</Table.HeaderCell>
                <Table.HeaderCell>Tipo venda</Table.HeaderCell>
                <Table.HeaderCell>ICCID</Table.HeaderCell>
                <Table.HeaderCell>Portabilidade</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {lines.map((line, idx) =>
                renderLineRow(line, `${dto.id}-${line.id}-${idx}`, {
                  showCheckboxColumn: showSwapCol,
                  allowRowCheckbox:
                    showSwapCol &&
                    lineIsAditivo(line) &&
                    !line.excluirDoContrato &&
                    !line.requerSuspensao,
                })
              )}
            </Table.Body>
          </Table>
        </div>
        {isFocus && showSwapCol && selectedLineIds.size > 0 ? (
          <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button color="blue" size="small" icon onClick={() => setSwapOpen(true)}>
              <Icon name="exchange" />
              Trocar FastChips ({selectedLineIds.size})
            </Button>
            <Button basic size="small" onClick={() => setSelectedLineIds(new Set())}>
              Limpar seleção
            </Button>
          </div>
        ) : null}
      </Segment>
    );
  };

  const swapLinesForModal = useMemo(() => {
    const lines = focusDto?.linhas ?? [];
    return lines
      .filter((l) => selectedLineIds.has(l.id))
      .map((l) => ({
        id: l.id,
        contractId: focusContractId ?? undefined,
        ddd: l.ddd,
        planName: l.planName || l.plano,
        chipCcid: l.chipCcid,
        negociacao: l.negociacao,
        isEsim: (l as ContractLineDto & { isEsim?: boolean }).isEsim,
      }));
  }, [focusDto?.linhas, selectedLineIds, focusContractId]);

  const currentIccidsOnContract = useMemo(
    () =>
      (focusDto?.linhas ?? [])
        .map((l) => l.chipCcid)
        .filter((x): x is string => typeof x === 'string' && x.length > 0),
    [focusDto?.linhas]
  );

  /** Primeiro chip Aditivo com CCID — alinha o stock de troca ao cadastro desse ICCID (API). */
  const referenceIccidForFastchipSwap = useMemo(() => {
    for (const l of focusDto?.linhas ?? []) {
      if (!lineIsAditivo(l)) continue;
      const chip = typeof l.chipCcid === 'string' ? l.chipCcid.trim() : '';
      if (!chip) continue;
      const digits = chip.replace(/\D/g, '');
      return digits.length > 0 ? digits : chip;
    }
    return undefined;
  }, [focusDto?.linhas]);

  if (!open) return null;

  return (
    <>
      <Modal open={open} onClose={onClose} size="fullscreen" closeIcon>
        <Modal.Header>
          <Icon name="linkify" />
          Contrato vinculado ao FAST
          {historyIcCidControl ? (
            <div style={{ marginTop: 8, fontWeight: 400, fontSize: '0.95rem', color: '#666' }}>
              ICCID <code>{historyIcCidControl.ccId}</code>
            </div>
          ) : null}
        </Modal.Header>
        <Modal.Content scrolling>
          {loading && (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <Loader active inline="centered" />
            </div>
          )}
          {!loading && groupLoadError && <Message warning>{groupLoadError}</Message>}
          {!loading && focusDto && contractBase && (
            <>
              <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <Button
                  basic
                  icon
                  labelPosition="left"
                  onClick={() => void openHistory()}
                  disabled={!historyIcCidControl && !fallbackHistoryChipCcId?.trim()}
                >
                  <Icon name="history" />
                  Histórico de movimentos FAST
                </Button>
              </div>
              <Message info size="small">
                Troca administrativa (bypass de estado do contrato) apenas para administradores e apenas em linhas{' '}
                <strong>Aditivo</strong> deste contrato FAST. Contratos do grupo (Reneg) são só leitura (RF13).
              </Message>
              {renderContractBlock(focusDto, 'focus')}
              {otherGroupDtos.length > 0 ? (
                <>
                  <Header as="h3" dividing style={{ marginTop: 24 }}>
                    Outros contratos do grupo
                  </Header>
                  {otherGroupDtos.map((d) => renderContractBlock(d, 'groupOther'))}
                </>
              ) : null}
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onClose}>Fechar</Button>
          {enableAdminContractCancel &&
            isAdmin &&
            contractBase &&
            normalizeContractStatus(contractBase.status) !== EContractStatus.Cancelado &&
            focusContractId && (
              <Button
                negative
                icon="ban"
                content="Cancelar contrato e libertar FAST"
                onClick={() => setCancelConfirmOpen(true)}
                disabled={loading}
              />
            )}
        </Modal.Actions>
      </Modal>

      <FastchipSwapModal
        open={swapOpen}
        onClose={() => setSwapOpen(false)}
        onConfirm={handleSwapConfirm}
        lines={swapLinesForModal}
        clientEstado={clientEstado}
        referenceIccid={referenceIccidForFastchipSwap}
        currentIccids={currentIccidsOnContract}
      />

      <FastChipMovementHistoryModal
        iccid={historyIcCid}
        open={historyOpen}
        onClose={() => {
          setHistoryOpen(false);
          setHistoryIcCid(null);
        }}
      />

      <Confirm
        open={cancelConfirmOpen}
        onCancel={() => !cancelLoading && setCancelConfirmOpen(false)}
        onConfirm={() => void executeCancelContract()}
        header="Confirmar cancelamento"
        content="O contrato será marcado como Cancelado (mesmo endpoint da página de Contratos). O FAST deve ser libertado pelo sistema em seguida. Deseja continuar?"
        confirmButton={{ content: 'Sim, cancelar contrato', negative: true, loading: cancelLoading }}
        cancelButton="Voltar"
      />
    </>
  );
};
